import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';

const TeamFilterChips = ({
  roles,
  roleFilter,
  venueOwnerships,
  venueFilter,
  teamFunctions,
  teamFunctionFilter,
  onRoleFilterChange,
  onVenueFilterChange,
  onTeamFunctionFilterChange,
  onClearFilters,
  membersByRole,
  membersByVenue,
  membersByTeamFunction,
  showDeleteSelected,
  onDeleteSelectedClick
}) => {
  const [openMenu, setOpenMenu] = useState(null); // 'role' | 'venue' | 'function' | null
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleRoleId = (roleId) => {
    const current = Array.isArray(roleFilter) ? roleFilter : [];
    const next = current.includes(roleId)
      ? current.filter(id => id !== roleId)
      : [...current, roleId];
    onRoleFilterChange(next);
  };

  const toggleVenueId = (venueOwnershipId) => {
    const current = Array.isArray(venueFilter) ? venueFilter : [];
    const next = current.includes(venueOwnershipId)
      ? current.filter(id => id !== venueOwnershipId)
      : [...current, venueOwnershipId];
    onVenueFilterChange(next);
  };

  const toggleTeamFunctionId = (teamFunctionId) => {
    const current = Array.isArray(teamFunctionFilter) ? teamFunctionFilter : [];
    const next = current.includes(teamFunctionId)
      ? current.filter(id => id !== teamFunctionId)
      : [...current, teamFunctionId];
    onTeamFunctionFilterChange(next);
  };

  const handleRemoveRoleFilter = (e) => {
    e.stopPropagation();
    onRoleFilterChange([]);
  };

  const handleRemoveVenueFilter = (e) => {
    e.stopPropagation();
    onVenueFilterChange([]);
  };

  const handleRemoveTeamFunctionFilter = (e) => {
    e.stopPropagation();
    onTeamFunctionFilterChange([]);
  };

  const hasRoleFilter = Array.isArray(roleFilter) && roleFilter.length > 0;
  const hasVenueFilter = Array.isArray(venueFilter) && venueFilter.length > 0;
  const hasTeamFunctionFilter = Array.isArray(teamFunctionFilter) && teamFunctionFilter.length > 0;

  const selectedRoleCount = hasRoleFilter
    ? roleFilter.reduce((sum, roleId) => sum + (membersByRole[roleId] || 0), 0)
    : 0;

  const selectedVenueCount = hasVenueFilter
    ? venueFilter.reduce((sum, venueId) => sum + (membersByVenue[venueId] || 0), 0)
    : 0;

  const selectedTeamFunctionCount = hasTeamFunctionFilter
    ? teamFunctionFilter.reduce((sum, fnId) => sum + ((membersByTeamFunction || {})[fnId] || 0), 0)
    : 0;

  const sortedRoles = [...roles].sort((a, b) => (
    (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' })
  ));

  const sortedTeamFunctions = [...(teamFunctions || [])].sort((a, b) => (
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  ));

  const sortedVenues = [...venueOwnerships].sort((a, b) => {
    const nameA = (a.venueable?.name || a.name || '').toLowerCase();
    const nameB = (b.venueable?.name || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });

  return (
    <div className="team-filters-container">
      {showDeleteSelected && (
        <button
          type="button"
          className="team-btn-danger-outline"
          onClick={onDeleteSelectedClick}
        >
          <i className="fal fa-trash-alt" style={{ fontSize: '14px' }}></i>
          <span>Delete selected</span>
        </button>
      )}

      {hasRoleFilter && (
        <div className="team-filter-chip">
          <span className="team-filter-chip-label">
            <span>Role</span>
            <span className="team-filter-chip-count">{selectedRoleCount}</span>
          </span>
          <i className="fal fa-chevron-down" style={{ fontSize: '12px', color: 'var(--sl-text-placeholder, #717680)' }}></i>
          <button
            onClick={handleRemoveRoleFilter}
            className="team-filter-chip-remove"
            aria-label="Remove role filter"
          >
            <i className="fal fa-times" style={{ fontSize: '14px' }}></i>
          </button>
        </div>
      )}

      {hasVenueFilter && (
        <div className="team-filter-chip">
          <span className="team-filter-chip-label">
            <span>Venue access</span>
            <span className="team-filter-chip-count">{selectedVenueCount}</span>
          </span>
          <i className="fal fa-chevron-down" style={{ fontSize: '12px', color: 'var(--sl-text-placeholder, #717680)' }}></i>
          <button
            onClick={handleRemoveVenueFilter}
            className="team-filter-chip-remove"
            aria-label="Remove venue filter"
          >
            <i className="fal fa-times" style={{ fontSize: '14px' }}></i>
          </button>
        </div>
      )}

      {hasTeamFunctionFilter && (
        <div className="team-filter-chip">
          <span className="team-filter-chip-label">
            <span>Function</span>
            <span className="team-filter-chip-count">{selectedTeamFunctionCount}</span>
          </span>
          <i className="fal fa-chevron-down" style={{ fontSize: '12px', color: 'var(--sl-text-placeholder, #717680)' }}></i>
          <button
            onClick={handleRemoveTeamFunctionFilter}
            className="team-filter-chip-remove"
            aria-label="Remove function filter"
          >
            <i className="fal fa-times" style={{ fontSize: '14px' }}></i>
          </button>
        </div>
      )}

      <div className="team-filter-dropdown-menus" ref={dropdownRef}>
        <div className="team-filter-dropdown">
          <button
            type="button"
            onClick={() => setOpenMenu(openMenu === 'role' ? null : 'role')}
            className="team-add-filter-btn"
          >
            <span>Role</span>
            <i className="fal fa-plus" style={{ fontSize: '14px' }}></i>
          </button>

          {openMenu === 'role' && (
            <div className="team-filter-dropdown-menu">
              <div className="team-filter-dropdown-header">
                Filter by Role
              </div>
              <div className="team-filter-dropdown-list">
                {sortedRoles.map((role) => {
                  const checked = Array.isArray(roleFilter) && roleFilter.includes(role.id);
                  return (
                    <label
                      key={role.id}
                      className="team-filter-dropdown-item"
                    >
                      <div className="team-filter-dropdown-item-left">
                        <input
                          type="checkbox"
                          className="team-filter-checkbox"
                          checked={checked}
                          onChange={() => toggleRoleId(role.id)}
                        />
                        <span>{role.display_name}</span>
                      </div>
                      <span className="team-filter-dropdown-count">
                        {membersByRole[role.id] || 0}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="team-filter-dropdown">
          <button
            type="button"
            onClick={() => setOpenMenu(openMenu === 'function' ? null : 'function')}
            className="team-add-filter-btn"
          >
            <span>Function</span>
            <i className="fal fa-plus" style={{ fontSize: '14px' }}></i>
          </button>

          {openMenu === 'function' && (
            <div className="team-filter-dropdown-menu">
              <div className="team-filter-dropdown-header">
                Filter by Function
              </div>
              <div className="team-filter-dropdown-list">
                {sortedTeamFunctions.map((fn) => {
                  const checked = Array.isArray(teamFunctionFilter) && teamFunctionFilter.includes(fn.id);
                  return (
                    <label
                      key={fn.id}
                      className="team-filter-dropdown-item"
                    >
                      <div className="team-filter-dropdown-item-left">
                        <input
                          type="checkbox"
                          className="team-filter-checkbox"
                          checked={checked}
                          onChange={() => toggleTeamFunctionId(fn.id)}
                        />
                        <span>{fn.name}</span>
                      </div>
                      <span className="team-filter-dropdown-count">
                        {(membersByTeamFunction || {})[fn.id] || 0}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="team-filter-dropdown">
          <button
            type="button"
            onClick={() => setOpenMenu(openMenu === 'venue' ? null : 'venue')}
            className="team-add-filter-btn"
          >
            <span>Venue access</span>
            <i className="fal fa-plus" style={{ fontSize: '14px' }}></i>
          </button>

          {openMenu === 'venue' && (
            <div className="team-filter-dropdown-menu">
              <div className="team-filter-dropdown-header">
                Filter by Venue Access
              </div>
              <div className="team-filter-dropdown-list">
                {sortedVenues.map((venueOwnership) => {
                  const id = venueOwnership.id;
                  const checked = Array.isArray(venueFilter) && venueFilter.includes(id);
                  return (
                    <label
                      key={id}
                      className="team-filter-dropdown-item"
                    >
                      <div className="team-filter-dropdown-item-left">
                        <input
                          type="checkbox"
                          className="team-filter-checkbox"
                          checked={checked}
                          onChange={() => toggleVenueId(id)}
                        />
                        <span>{venueOwnership.venueable?.name || venueOwnership.name}</span>
                      </div>
                      <span className="team-filter-dropdown-count">
                        {membersByVenue[id] || 0}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TeamFilterChips.propTypes = {
  roles: PropTypes.array.isRequired,
  roleFilter: PropTypes.array,
  venueOwnerships: PropTypes.array.isRequired,
  venueFilter: PropTypes.array,
  teamFunctions: PropTypes.array,
  teamFunctionFilter: PropTypes.array,
  onRoleFilterChange: PropTypes.func.isRequired,
  onVenueFilterChange: PropTypes.func.isRequired,
  onTeamFunctionFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  membersByRole: PropTypes.object.isRequired,
  membersByVenue: PropTypes.object.isRequired,
  membersByTeamFunction: PropTypes.object,
  showDeleteSelected: PropTypes.bool,
  onDeleteSelectedClick: PropTypes.func
};

export default TeamFilterChips;
